import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Mail, Send, Inbox, Plus, Loader2, Trash2, Paperclip, Users, User, Eye, ChevronRight, FileText, Download } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MessagesPage() {
  const { user, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('inbox');
  const [inboxMessages, setInboxMessages] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ unread_count: 0, sent_count: 0 });
  const [loading, setLoading] = useState(true);
  
  const [composeOpen, setComposeOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [form, setForm] = useState({
    subject: '',
    content: '',
    recipients: [],
    send_to_all: false,
    file_url: null,
    file_name: null
  });

  const fetchData = useCallback(async () => {
    try {
      const [inboxRes, sentRes, statsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/internal-messages/inbox`),
        axios.get(`${API_URL}/api/internal-messages/sent`),
        axios.get(`${API_URL}/api/internal-messages/stats`),
        axios.get(`${API_URL}/api/users/directory`)  // Use directory endpoint accessible by all users
      ]);
      setInboxMessages(inboxRes.data);
      setSentMessages(sentRes.data);
      setStats(statsRes.data);
      setUsers(usersRes.data.filter(u => u.id !== user?.id));
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!form.subject || !form.content) {
      toast.error('Veuillez remplir le sujet et le contenu');
      return;
    }
    if (!form.send_to_all && form.recipients.length === 0) {
      toast.error('Veuillez sélectionner au moins un destinataire ou "Envoyer à tous"');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/internal-messages`, form);
      toast.success('Message envoyé');
      setComposeOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    try {
      await axios.delete(`${API_URL}/api/internal-messages/${messageId}`);
      toast.success('Message supprimé');
      setViewOpen(false);
      setSelectedMessage(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleViewMessage = async (message) => {
    setSelectedMessage(message);
    setViewOpen(true);
    
    if (!message.read_by?.includes(user?.id) && message.sender_id !== user?.id) {
      try {
        await axios.put(`${API_URL}/api/internal-messages/${message.id}/read`);
        fetchData();
      } catch (error) {
        console.error('Error marking as read:', error);
      }
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux (max 10 Mo)');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(`${API_URL}/api/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm(prev => ({
        ...prev,
        file_url: res.data.url,
        file_name: file.name
      }));
      toast.success('Fichier téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = () => {
    setForm(prev => ({ ...prev, file_url: null, file_name: null }));
  };

  const resetForm = () => {
    setForm({
      subject: '',
      content: '',
      recipients: [],
      send_to_all: false,
      file_url: null,
      file_name: null
    });
  };

  const toggleRecipient = (userId) => {
    setForm(prev => {
      if (prev.recipients.includes(userId)) {
        return { ...prev, recipients: prev.recipients.filter(id => id !== userId) };
      } else {
        return { ...prev, recipients: [...prev.recipients, userId] };
      }
    });
  };

  const isUnread = (message) => {
    return !message.read_by?.includes(user?.id) && message.sender_id !== user?.id;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="messages-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Messages</h1>
          <p className="text-slate-500 mt-1">Envoyez et recevez des messages internes</p>
        </div>
        <Button onClick={() => setComposeOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="compose-btn">
          <Plus className="w-4 h-4 mr-2" />Nouveau message
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-600 to-blue-700 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-200 text-sm">Non lus</p>
                <p className="text-3xl font-bold mt-1">{stats.unread_count}</p>
              </div>
              <Inbox className="w-10 h-10 text-blue-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Envoyés</p>
                <p className="text-3xl font-bold mt-1 text-slate-900">{stats.sent_count}</p>
              </div>
              <Send className="w-10 h-10 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Reçus</p>
                <p className="text-3xl font-bold mt-1 text-slate-900">{inboxMessages.length}</p>
              </div>
              <Mail className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Messages List */}
      <Card>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <CardHeader className="pb-0">
            <TabsList>
              <TabsTrigger value="inbox" data-testid="inbox-tab">
                <Inbox className="w-4 h-4 mr-2" />
                Reçus
                {stats.unread_count > 0 && (
                  <Badge className="ml-2 bg-red-500">{stats.unread_count}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="sent" data-testid="sent-tab">
                <Send className="w-4 h-4 mr-2" />
                Envoyés
              </TabsTrigger>
            </TabsList>
          </CardHeader>
          <CardContent className="pt-4">
            <TabsContent value="inbox" className="mt-0">
              {inboxMessages.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Inbox className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Aucun message reçu</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {inboxMessages.map((msg) => (
                    <div 
                      key={msg.id} 
                      className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-colors ${
                        isUnread(msg) ? 'bg-blue-50 hover:bg-blue-100 border-l-4 border-l-blue-500' : 'bg-slate-50 hover:bg-slate-100'
                      }`}
                      onClick={() => handleViewMessage(msg)}
                      data-testid={`message-item-${msg.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-semibold ${isUnread(msg) ? 'text-slate-900' : 'text-slate-600'}`}>
                            {msg.sender_name}
                          </span>
                          {msg.send_to_all && (
                            <Badge variant="outline" className="text-xs">
                              <Users className="w-3 h-3 mr-1" />Tous
                            </Badge>
                          )}
                          {msg.file_url && (
                            <Badge variant="secondary" className="text-xs">
                              <Paperclip className="w-3 h-3 mr-1" />Pièce jointe
                            </Badge>
                          )}
                          {isUnread(msg) && (
                            <Badge className="bg-blue-500 text-xs">Nouveau</Badge>
                          )}
                        </div>
                        <p className={`text-sm truncate ${isUnread(msg) ? 'font-medium text-slate-800' : 'text-slate-600'}`}>
                          {msg.subject}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {format(new Date(msg.created_at), 'dd MMM yyyy à HH:mm', { locale: fr })}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="sent" className="mt-0">
              {sentMessages.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Send className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Aucun message envoyé</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {sentMessages.map((msg) => (
                    <div 
                      key={msg.id} 
                      className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
                      onClick={() => handleViewMessage(msg)}
                      data-testid={`sent-message-${msg.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-slate-500 text-sm">À :</span>
                          {msg.send_to_all ? (
                            <Badge variant="outline" className="text-xs">
                              <Users className="w-3 h-3 mr-1" />Tous les utilisateurs
                            </Badge>
                          ) : (
                            <span className="font-medium text-slate-700 truncate">
                              {msg.recipient_names?.join(', ') || 'N/A'}
                            </span>
                          )}
                          {msg.file_url && (
                            <Badge variant="secondary" className="text-xs">
                              <Paperclip className="w-3 h-3 mr-1" />Pièce jointe
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-800 truncate">{msg.subject}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {format(new Date(msg.created_at), 'dd MMM yyyy à HH:mm', { locale: fr })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); handleDelete(msg.id); }}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>

      {/* Compose Dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nouveau message</DialogTitle></DialogHeader>
          <form onSubmit={handleSend} className="space-y-4">
            <div className="space-y-2">
              <Label>Sujet *</Label>
              <Input 
                value={form.subject}
                onChange={(e) => setForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Objet du message"
                data-testid="message-subject"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea 
                value={form.content}
                onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Écrivez votre message..."
                rows={6}
                data-testid="message-content"
              />
            </div>
            
            {/* Recipients */}
            <div className="space-y-3">
              <Label>Destinataires</Label>
              {isAdmin && (
                <div className="flex items-center gap-2 mb-2">
                  <Checkbox 
                    id="send-to-all"
                    checked={form.send_to_all}
                    onCheckedChange={(checked) => setForm(prev => ({ ...prev, send_to_all: checked, recipients: [] }))}
                  />
                  <label htmlFor="send-to-all" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Envoyer à tous les utilisateurs
                  </label>
                </div>
              )}
              
              {!form.send_to_all && (
                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                  {users.map(u => (
                    <div key={u.id} className="flex items-center gap-2">
                      <Checkbox 
                        id={`recipient-${u.id}`}
                        checked={form.recipients.includes(u.id)}
                        onCheckedChange={() => toggleRecipient(u.id)}
                      />
                      <label htmlFor={`recipient-${u.id}`} className="text-sm cursor-pointer flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        {u.first_name} {u.last_name}
                        <span className="text-slate-400">({u.department || 'N/A'})</span>
                        {u.role === 'admin' && <Badge className="text-xs">Admin</Badge>}
                      </label>
                    </div>
                  ))}
                </div>
              )}
              {form.recipients.length > 0 && !form.send_to_all && (
                <p className="text-xs text-slate-500">{form.recipients.length} destinataire(s) sélectionné(s)</p>
              )}
            </div>
            
            {/* File Attachment */}
            <div className="space-y-2">
              <Label>Pièce jointe</Label>
              {form.file_url ? (
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-500" />
                    <span className="text-sm font-medium">{form.file_name}</span>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={removeFile}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="cursor-pointer"
                    data-testid="file-upload"
                  />
                  {uploading && <Loader2 className="w-4 h-4 animate-spin" />}
                </div>
              )}
              <p className="text-xs text-slate-400">Taille maximale : 10 Mo</p>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setComposeOpen(false); resetForm(); }}>
                Annuler
              </Button>
              <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="send-message-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>
                    <Send className="w-4 h-4 mr-2" />Envoyer
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Message Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedMessage?.subject}</DialogTitle>
          </DialogHeader>
          
          {selectedMessage && (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">De :</span>
                  <span className="font-medium">{selectedMessage.sender_name}</span>
                </div>
                <span className="text-slate-400">
                  {format(new Date(selectedMessage.created_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                </span>
              </div>
              
              <div className="text-sm">
                <span className="text-slate-500">À :</span>{' '}
                {selectedMessage.send_to_all ? (
                  <Badge variant="outline">Tous les utilisateurs</Badge>
                ) : (
                  <span className="font-medium">{selectedMessage.recipient_names?.join(', ')}</span>
                )}
              </div>
              
              <div className="border-t pt-4">
                <p className="whitespace-pre-wrap text-slate-700">{selectedMessage.content}</p>
              </div>
              
              {selectedMessage.file_url && (
                <div className="border-t pt-4">
                  <p className="text-sm text-slate-500 mb-2">Pièce jointe :</p>
                  <a 
                    href={`${API_URL}${selectedMessage.file_url}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <FileText className="w-5 h-5 text-blue-500" />
                    <span className="text-sm font-medium flex-1">{selectedMessage.file_name}</span>
                    <Download className="w-4 h-4 text-slate-400" />
                  </a>
                </div>
              )}
              
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Eye className="w-4 h-4" />
                  Lu par {selectedMessage.read_by?.length || 0} personne(s)
                </div>
                {(selectedMessage.sender_id === user?.id || isAdmin) && (
                  <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(selectedMessage.id)}>
                    <Trash2 className="w-4 h-4 mr-2" />Supprimer
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
